import { useEffect, useRef } from "react";

// While something is open over the times, one history entry stands for it, so the back gesture
// (Chrome's swipe, Android's back) closes it instead of leaving the Home app.
export const useBackCloses = (open: boolean, closeAll: () => void) => {
  const close = useRef(closeAll);
  close.current = closeAll;
  const isOpen = useRef(open);
  isOpen.current = open;
  // A ref, not effect cleanup: StrictMode re-runs effects, and a stray history.back() would
  // close what was just opened.
  const pushed = useRef(false);
  // Our own history.back() lands later, aimed at the entry before ours. Something opened before
  // it lands (Listen closes Search, then the player opens) would lose its entry to it, so it
  // pushes once it has landed.
  const goingBack = useRef(false);

  useEffect(() => {
    const onBack = () => {
      if (goingBack.current) {
        goingBack.current = false;
        if (isOpen.current) {
          window.history.pushState({ homeOverlay: true }, "");
          pushed.current = true;
        }
        return;
      }
      if (!pushed.current) return;
      pushed.current = false;
      close.current();
    };
    window.addEventListener("popstate", onBack);
    return () => window.removeEventListener("popstate", onBack);
  }, []);

  useEffect(() => {
    if (open && !pushed.current && !goingBack.current) {
      window.history.pushState({ homeOverlay: true }, "");
      pushed.current = true;
    } else if (!open && pushed.current) {
      // Closed with a button: take the entry back, so the next back leaves as usual.
      pushed.current = false;
      goingBack.current = true;
      window.history.back();
    }
  }, [open]);
};
